import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      } else {
        setError('Credenziali non valide');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1602577121285-aca49920efac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwd2hpdGV8ZW58MHx8fHwxNzc1MzExNjI4fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="w-full h-full bg-zinc-900/40" />
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Logo size="2xl" />
          </div>

          <p className="text-zinc-500 mb-8">Inserisci le tue credenziali per continuare</p>

          <form onSubmit={handleSubmit} className="space-y-6 text-left">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wider text-zinc-700 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="email"
                className="rounded-none border-zinc-300 focus:ring-zinc-900 focus:border-zinc-900"
                placeholder="nome@esempio.com"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wider text-zinc-700 block">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="current-password"
                className="rounded-none border-zinc-300 focus:ring-zinc-900 focus:border-zinc-900"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
              <div>
                <Link to="/forgot-password" className="text-sm text-zinc-500 hover:text-zinc-900" data-testid="forgot-password-link">
                  Password dimenticata?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6"
              data-testid="login-submit-button"
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>

          <p className="mt-8 text-zinc-500">
            Non hai un account?{' '}
            <Link to="/register" className="text-zinc-900 font-bold hover:underline" data-testid="register-link">
              Registrati
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
